import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { useGhgOrganization } from 'src/common/hooks';
import { timeConverter } from 'src/utils/timeConverter';
import QuestionnaireTitle from 'src/views/components/Questionnaire/Title';
import {
  PageContainer,
  SectionGroupContainer,
} from 'src/views/Pages/Questionnaire/index.styles';
import IntuitiveCalendar from 'src/views/components/IntuitiveCalendar';
import { StyledPanel } from 'src/components/StyledPanel';
import {
  GhgReportExportOrganization,
  useGhgReportExport,
  useGhgReportExportOptions,
} from '../hooks';

type OrganizationNodeProps = {
  organization: GhgReportExportOrganization;
  organizations: GhgReportExportOrganization[];
  selectedIds: number[];
  depth?: number;
  setSelectedIds: (_ids: number[]) => void;
};

const getNestedOrgIds = (
  organizations: GhgReportExportOrganization[],
  orgId: number
): number[] => [
  orgId,
  ...organizations
    .filter((item) => item.parent_id === orgId)
    .flatMap((item) => getNestedOrgIds(organizations, item.id)),
];

function OrganizationNode({
  organization,
  organizations,
  selectedIds,
  depth = 0,
  setSelectedIds,
}: OrganizationNodeProps) {
  const childOrganizations = organizations.filter(
    (item) => item.parent_id === organization.id
  );
  const nestedIds = getNestedOrgIds(organizations, organization.id);
  const checked = nestedIds.every((id) => selectedIds.includes(id));
  const indeterminate =
    !checked && nestedIds.some((id) => selectedIds.includes(id));

  const handleChange = () => {
    const selectedSet = new Set(selectedIds);
    if (checked || indeterminate) {
      nestedIds.forEach((id) => selectedSet.delete(id));
    } else {
      nestedIds.forEach((id) => selectedSet.add(id));
    }
    setSelectedIds(Array.from(selectedSet));
  };

  return (
    <Box>
      <FormControlLabel
        sx={{ ml: depth * 2 }}
        control={
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={handleChange}
          />
        }
        label={organization.name}
      />
      {childOrganizations.map((childOrganization) => (
        <OrganizationNode
          key={childOrganization.id}
          organization={childOrganization}
          organizations={organizations}
          selectedIds={selectedIds}
          depth={depth + 1}
          setSelectedIds={setSelectedIds}
        />
      ))}
    </Box>
  );
}

const getInitialStartDate = () => new Date(new Date().getFullYear(), 0, 1);
const getInitialEndDate = () => new Date(new Date().getFullYear(), 11, 1);
const EMPTY_ORGANIZATIONS: GhgReportExportOrganization[] = [];

export default function GhgReportExportPage() {
  const { t } = useTranslation();
  const organization = useGhgOrganization();
  const reportExportOptions = useGhgReportExportOptions(organization.id);
  const reportExport = useGhgReportExport();

  const reportingFreq =
    reportExportOptions.data?.reporting_freq || organization.co2.reporting_freq;
  const organizations =
    reportExportOptions.data?.organizations ?? EMPTY_ORGANIZATIONS;
  const organizationIds = React.useMemo(
    () => organizations.map((item) => item.id),
    [organizations]
  );
  const topOrganizations = React.useMemo(
    () =>
      organizations.filter(
        (item) => !organizationIds.includes(item.parent_id || 0)
      ),
    [organizationIds, organizations]
  );

  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [reportStartTime, setReportStartTime] = React.useState<Date | null>(
    getInitialStartDate()
  );
  const [reportEndTime, setReportEndTime] = React.useState<Date | null>(
    getInitialEndDate()
  );

  React.useEffect(() => {
    setSelectedIds(organizationIds);
  }, [organizationIds]);

  const handleSelectAll = () => setSelectedIds(organizationIds);
  const handleClearAll = () => setSelectedIds([]);

  const handleExport = async () => {
    if (!reportStartTime || !reportEndTime || selectedIds.length === 0) return;

    await reportExport.mutateAsync({
      organizationId: organization.id,
      organizationIds: selectedIds,
      startPeriod: timeConverter(reportStartTime, reportingFreq),
      endPeriod: timeConverter(reportEndTime, reportingFreq),
      reportingFreq,
    });
  };

  let organizationsContent = (
    <Box>
      {topOrganizations.map((item) => (
        <OrganizationNode
          key={item.id}
          organization={item}
          organizations={organizations}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />
      ))}
    </Box>
  );
  if (reportExportOptions.isLoading) {
    organizationsContent = (
      <Stack alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  } else if (organizations.length === 0) {
    organizationsContent = (
      <Alert severity="info">{t('ghg:reportExport.empty')}</Alert>
    );
  }

  return (
    <PageContainer>
      <SectionGroupContainer>
        <QuestionnaireTitle text={t('ghg:reportExport.title')} />

        <StyledPanel>
          <Stack spacing={3}>
            <Typography variant="h6">
              {t('ghg:reportExport.periodTitle')}
            </Typography>
            <IntuitiveCalendar
              reportingPeriod={reportingFreq}
              startDate={reportStartTime}
              setStartDate={setReportStartTime}
              endDate={reportEndTime}
              setEndDate={setReportEndTime}
            />
          </Stack>
        </StyledPanel>

        <StyledPanel>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="h6">
                {t('ghg:reportExport.organizationsTitle')}
              </Typography>
              <Stack direction="row" gap={1}>
                <Button size="small" onClick={handleClearAll}>
                  {t('ghg:reportExport.actions.clear')}
                </Button>
                <Button size="small" onClick={handleSelectAll}>
                  {t('ghg:reportExport.actions.selectAll')}
                </Button>
              </Stack>
            </Stack>

            {organizationsContent}

            <Button
              variant="contained"
              startIcon={
                reportExport.isLoading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={
                reportExport.isLoading ||
                !reportStartTime ||
                !reportEndTime ||
                selectedIds.length === 0
              }
              onClick={handleExport}
            >
              {reportExport.isLoading
                ? t('ghg:reportExport.actions.exporting')
                : t('ghg:reportExport.actions.export')}
            </Button>
          </Stack>
        </StyledPanel>
      </SectionGroupContainer>
    </PageContainer>
  );
}
